/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState } from "react";

import { default as classNames, default as cn } from "classnames";
import { useLocation, useNavigate } from "react-router-dom";

import { ROUTES } from "Router";

import { getAvatar } from "models/User";
import { useAuth, useUI } from "state";

import { PrimaryEventsMenu } from "components/common";
import {
  AdminsIcon,
  ChevronDown,
  ExpandIcon,
  Logo,
  LogoutIcon,
} from "components/icons";
import BellIcon from "components/icons/common/BellIcon";
import NoNotificationsIcon from "components/icons/common/NoNotificationsIcon";

import useNotifications from "pages/Notifications/useNotifications";

import { SearchInput } from "../Input";

import NotificationCard from "./NotificationCard";

import { usePrimaryEvents } from "state";
import { Button, LinkItem } from "../Button";
import DropDown from "../DropDown";
import Stack from "../Stack";
import Heading from "../Heading";
import { Spinner } from "../Loaders";
import Text from "../Text";
import Avatar from "../Avatar";

const Header = ({ isSidebarRendered }: { isSidebarRendered: boolean }) => {
  const {
    toggleSidebar,
    currentRouteOptions,
    isSidebarShrunk,
    setSidebarShrunk,
  } = useUI();

  const [isSearchToggled, setSearchToggled] = useState(false);
  const { hasSelectedEvent, handleRemoveEvent } = usePrimaryEvents();
  const { pathname } = useLocation();
  const isWorkspacesRoute = pathname.includes("/workspaces/p/");
  const navigate = useNavigate();
  return (
    <header
      className="bg-c-gray-700 z-[50] text-white fixed top-0 inset-x-0 h-[var(--spacing-60)] print:hidden"
      test-id="-header"
    >
      <div
        className="flex justify-end px-2 xs:px-3 sm:px-s30 py-2 relative h-full items-center"
        test-id="-header-container"
      >
        <div
          className={cn(
            "flex w-1/2 justify-start items-center h-[40px]",
            isSearchToggled &&
              currentRouteOptions.hasSearchInput &&
              "hidden xs:flex",
          )}
          test-id="-header-logo-container"
        >
          <div className="w-[200px] flex items-center justify-between">
            <div>
              <Button
                variant="unstyled"
                className="group"
                onClick={() => {
                  if (isWorkspacesRoute) return;
                  toggleSidebar(false);

                  if (hasSelectedEvent) handleRemoveEvent();
                  else navigate(ROUTES.WORKSPACES);
                }}
                test-id="sidebar-logo-button"
              >
                <div
                  className={classNames(
                    isWorkspacesRoute ? "hidden" : "group-hover:flex hidden",
                    "w-full cursor-pointer bg-[#3C3C3C] rounded items-center gap-x-1 p-3 shadow-lg",
                  )}
                >
                  <ChevronDown className="rotate-90" /> All Workspaces
                </div>

                <Logo
                  className={classNames(
                    isWorkspacesRoute ? "flex" : "group-hover:hidden flex",
                    "h-14 object-contain   pt-[1rem] pb-[.5rem] sm:pb-0 sm:pt-2",
                  )}
                  test-id="IHP56djT"
                />
              </Button>
            </div>

            {isSidebarRendered && (
              <div className="hidden sm:block">
                <Button
                  variant="unstyled"
                  onClick={() => setSidebarShrunk(!isSidebarShrunk)}
                  test-id="sidebar-expand-button"
                >
                  <ExpandIcon
                    className={cn(
                      "origin-center duration-300",
                      isSidebarShrunk ? "rotate-180" : "rotate-0",
                    )}
                  />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div
          className="h-[40px] hidden lg:block"
          test-id="-primary-events-menu"
        >
          <PrimaryEventsMenu />
        </div>

        <div
          className={cn(
            "flex justify-end items-center h-[40px] z-[1] space-x-s5",
            isSearchToggled ? "w-full" : "w-1/2",
          )}
          test-id="-search-container"
        >
          {/* <div
             className={cn(
               currentRouteOptions.hasSearchInput ? "flex sm:hidden" : "hidden"
             )}
            > */}

          <div className="hidden" test-id="DJ9A26NS">
            <SearchInput
              variant="header"
              onSearch={currentRouteOptions.onSearch}
              onToggle={() => setSearchToggled((prev) => !prev)}
              isLoading={currentRouteOptions.isSearching}
              test-id="-search-input"
            />
          </div>

          {!isSearchToggled && (
            <>
              {isSidebarRendered && hasSelectedEvent && (
                <NotificationButton test-id="-notification-button" />
              )}
              <UserDropDown test-id="-user-drop-down" />
            </>
          )}

          {isSidebarRendered && (
            <div className="sm:hidden h-[40px]" test-id="E0dtMe_k">
              <Button
                variant="ghost"
                size="small"
                className="justify-end w-fit"
                onClick={() => toggleSidebar()}
                test-id="0ueQF6aE"
              >
                <span test-id="zbxIzU__">
                  <MenuIcon test-id="XAvqdi5-" />
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;

export const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth={0.1}
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
    />
  </svg>
);

const DropDownButton = () => {
  const { data, isFetched, isFetching, handleTapToView } = useNotifications();
  const { toggleSidebar } = useUI();
  return (
    <DropDown
      variant="secondary"
      maxHeight={500}
      btnProps={{
        text: (
          <span className="relative" test-id="Ebb7XZeO">
            {data?.unseen_notifications && (
              <div
                className="bg-alert h-2 w-2 rounded-full absolute -top-1 -right-1"
                test-id="KPUSgqhv"
              />
            )}

            <BellIcon test-id="SZA3bHMA" />
          </span>
        ),

        variant: "secondary",
        size: "small",
        className: "h-[40px]",
        hasChevron: false,
        onClick: () => {
          toggleSidebar(false);
        },
      }}
      pos="right"
      listClassName="mt-s15 !z-[100]"
      test-id="xNodw9nQ"
    >
      {(close) => (
        <Stack
          className="p-s30 h-full space-y-s20 !w-[450px]"
          test-id="j0s-tsff"
        >
          <div
            className="flex space-x-3 items-center justify-between"
            test-id="fmAfsyYA"
          >
            <Heading as="h2" test-id="3b8_mgAR">
              Notifications
            </Heading>

            <div test-id="O2KIU_EO">
              <LinkItem
                variant="primary-outline"
                size="large"
                slug={ROUTES.NOTIFICATIONS}
                onClick={() => {
                  close();
                }}
                test-id="WqrEDjDw"
              >
                <span className="whitespace-nowrap" test-id="5W1mPs_J">
                  See All
                </span>
              </LinkItem>
            </div>
          </div>

          <Stack
            className="relative space-y-s10 pb-s30 h-full"
            test-id="Kbn8ji-O"
          >
            {isFetching && (
              <span
                className="flex items-center justify-center inset-0 absolute"
                test-id="X5B8zvGS"
              >
                <Spinner test-id="XYr2G9h5" />
              </span>
            )}

            {!isFetching &&
              isFetched &&
              data &&
              data.data &&
              Object.keys(data.data).map((date) => {
                const notifications = data.data[date];
                return (
                  <Stack key={date} className="space-y-s30" test-id="h6hZQEoS">
                    {notifications.map((notification: any) => (
                      <NotificationCard
                        key={notification.id}
                        notification={notification}
                        handleTapToView={handleTapToView}
                        onClick={() => {
                          close();
                        }}
                        test-id="5Ju0RPYU"
                      />
                    ))}
                  </Stack>
                );
              })}

            <Stack className="justify-center h-full" test-id="_pebqT_R">
              {isFetched &&
                data &&
                data.data &&
                !Object.keys(data.data).length && (
                  // No notifications available
                  <Stack className="text-center mt-5" test-id="p5PsPmcO">
                    <div className="mx-auto mb-4" test-id="VduRZkpf">
                      <NoNotificationsIcon test-id="3ogeBMYC" />
                    </div>

                    <Text
                      className="text-light font-bold mb-1"
                      test-id="t7g2D039"
                    >
                      You have no Notifications yet.
                    </Text>
                  </Stack>
                )}
            </Stack>
          </Stack>
        </Stack>
      )}
    </DropDown>
  );
};

const NotificationButton = () => {
  return (
    <div className="w-[45px]" test-id="Sa-_qqQ6">
      <div className="hidden sm:block w-full h-full" test-id="Zd1lHMSu">
        <DropDownButton test-id="twGC34O2" />
      </div>

      <div className="w-full h-full sm:hidden" test-id="C7Lf2gZV">
        <LinkItem
          size="large"
          variant="unstyled"
          slug={ROUTES.NOTIFICATIONS}
          test-id="2M4_u6jf"
        >
          <span className="relative" test-id="Tsr1YCN9">
            <div
              className="bg-alert h-2 w-2 rounded-full absolute -top-1 -right-1"
              test-id="cQ4_oSDa"
            />
            <BellIcon test-id="VkVLZ5cm" />
          </span>
        </LinkItem>
      </div>
    </div>
  );
};

const UserDropDown = () => {
  const { user, handleLogout } = useAuth();
  const { toggleSidebar } = useUI();
  return (
    <div test-id="y8qby1pH">
      <DropDown
        listClassName="mt-s5 !z-[100]"
        containerClassName="h-[40px] "
        pos="right"
        btnProps={{
          text: (
            <div
              className="flex items-center space-x-s10 mr-s10 w-12 sm:w-32"
              test-id="ykM-MMiH"
            >
              <Avatar
                src={getAvatar(user)}
                alt={user?.firstName}
                size="xxs"
                test-id="YnWKkzzM"
              />

              <Text
                as="span"
                className="font-normal capitalize hidden sm:inline sm:w-[70px] truncate text-left"
                test-id="zNcMHx7m"
              >
                {user?.firstName}
              </Text>
            </div>
          ),

          variant: "secondary",
          size: "small",
          className:
            "h-[40px] pr-0 w-10 sm:w-40 !bg-transparent sm:!bg-secondary border-none",
          hasChevron: window.innerWidth > 768 ? true : false,
          onClick: () => {
            toggleSidebar(false);
          },
        }}
      >
        {(close) => (
          <Stack>
            <div>
              <Button
                variant="ghost"
                onClick={() => {
                  close();
                  window.location.replace(user?.profile);
                }}
                test-id="H0rUzbiR"
              >
                <div
                  className="flex items-center w-56 text-white space-x-s20 px-s20 py-s10"
                  test-id="ObquwnoB"
                >
                  <AdminsIcon
                    fill="#fff"
                    className="w-4 h-4"
                    test-id="mfmCbDa-"
                  />
                  <Text>User Profile</Text>
                </div>
              </Button>
            </div>

            <div test-id="4C_WkJ83">
              <Button
                variant="ghost"
                onClick={() => {
                  close();
                  handleLogout();
                }}
                id="logoutBtn"
                test-id="KjwucmEN"
              >
                <div
                  className="flex items-center w-56 text-white space-x-s20 px-s20 py-s10"
                  test-id="muwJXJDN"
                >
                  <LogoutIcon
                    isActive
                    className="w-5 h-5 -ml-0.5 -mt-1"
                    test-id="SKeU7LLo"
                  />
                  <Text test-id="MacNvf27">Logout</Text>
                </div>
              </Button>
            </div>
          </Stack>
        )}
      </DropDown>
    </div>
  );
};
